import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface EditorialInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const EditorialInsights: React.FC<EditorialInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Soft elegant fade transition
  const opacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const translateY = interpolate(elapsedFrames, [0, 10], [10, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 12], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 16, mass: 0.6, stiffness: 95 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "#fdfcf7",
          border: "1px solid rgba(100, 80, 60, 0.15)",
          borderRadius: "2px", // sharp editorial borders!
          boxShadow: "0 15px 35px rgba(100, 80, 60, 0.05)",
          padding: "50px 40px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          position: "relative",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Top vintage corner markers */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            width: "15px",
            height: "15px",
            borderTop: "1px solid rgba(100, 80, 60, 0.4)",
            borderLeft: "1px solid rgba(100, 80, 60, 0.4)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            width: "15px",
            height: "15px",
            borderTop: "1px solid rgba(100, 80, 60, 0.4)",
            borderRight: "1px solid rgba(100, 80, 60, 0.4)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontStyle: "italic",
              fontWeight: 600,
              fontSize: "18px",
              color: "#6b532f",
            }}
          >
            Insight Section 0{activeIndex + 1}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#6b532f"
                accentColor="#6b532f"
                size={68}
                style="minimal"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        <div
          style={{
            opacity,
            transform: `translateY(${translateY}px)`,
            minHeight: "290px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Large editorial quote marks */}
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "76px",
              lineHeight: 1,
              color: "rgba(107, 83, 47, 0.15)",
              marginBottom: "-20px",
            }}
          >
            “
          </span>
          <p
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "50px", // serif geometric scales nicely at 50px
              fontWeight: 700,
              lineHeight: 1.4,
              color: "#281e14", // dark wood slate
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 600,
                color: "#6b532f",
                fontStyle: "italic",
                textDecoration: "underline",
                textDecorationColor: "rgba(107,83,47,0.3)",
                textUnderlineOffset: "4px",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "76px",
              lineHeight: 1,
              color: "rgba(107, 83, 47, 0.15)",
              marginTop: "5px",
            }}
          >
            ”
          </span>
        </div>

        {/* Dot pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            alignItems: "center",
            borderTop: "1px solid rgba(100,80,60,0.15)",
            paddingTop: "16px",
          }}
        >
          {bullets.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeIndex ? 20 : 6,
                height: 6,
                borderRadius: 3,
                background:
                  i === activeIndex ? "#6b532f" : "rgba(107,83,47,0.2)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
